import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar, Input, Radio, Button, Card, Toast } from 'antd-mobile';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './MobileEditPlayer.css';

/**
 * 移动端编辑球员组件
 * 可以修改球员的名字、性别等基础信息
 */
const MobileEditPlayer = () => {
  const navigate = useNavigate();
  const { id, zone } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    gender: 'MALE',
    age: '',
    level: '',
    organization: '',
    phone: '',
    email: '',
    notes: ''
  });

  // 获取球员信息
  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        setFetching(true);
        const baseUrl = getApiBaseUrl();
        const response = await axios.get(`${baseUrl}/api/${zone}/players/${id}`);
        
        if (response.data.success) {
          const player = response.data.data;
          setFormData({
            name: player.name || '',
            gender: player.gender || 'MALE',
            age: player.age ? String(player.age) : '',
            level: player.level || '',
            organization: player.organization || '',
            phone: player.phone || '',
            email: player.email || '',
            notes: player.notes || ''
          });
        } else {
          Toast.show({
            content: response.data.message || '获取球员信息失败',
            position: 'center'
          });
        }
      } catch (error) {
        console.error('获取球员信息失败:', error);
        Toast.show({
          content: error.response?.data?.message || '获取球员信息失败',
          position: 'center'
        });
      } finally {
        setFetching(false);
      }
    };

    if (id && zone) {
      fetchPlayerData();
    }
  }, [id, zone]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // 验证必填项
    if (!formData.name || !formData.name.trim()) {
      Toast.show({
        content: '请输入姓名',
        position: 'center'
      });
      return;
    }

    if (!formData.gender) {
      Toast.show({
        content: '请选择性别',
        position: 'center'
      });
      return;
    }

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();

      // 准备更新数据
      const updateData = {
        name: formData.name.trim(),
        gender: formData.gender
      };

      // 可选字段 - 允许清空，所以总是发送（空字符串或null）
      if (formData.age && formData.age.trim() !== '') {
        const ageValue = parseInt(formData.age, 10);
        if (!isNaN(ageValue) && ageValue >= 0 && ageValue <= 120) {
          updateData.age = ageValue;
        }
      } else {
        // 如果清空了年龄，设置为null以删除该字段
        updateData.age = null;
      }
      
      // 水平字段
      updateData.level = formData.level || null;
      
      // 组织字段 - 允许清空
      updateData.organization = formData.organization.trim() || null;
      
      // 手机号字段 - 允许清空
      updateData.phone = formData.phone.trim() || null;
      
      // 邮箱字段 - 允许清空
      updateData.email = formData.email.trim() || null;
      
      // 备注字段 - 允许清空
      updateData.notes = formData.notes.trim() || null;

      const response = await axios.put(
        `${baseUrl}/api/${zone}/players/${id}`,
        updateData
      );

      if (response.data.success) {
        Toast.show({
          content: '更新成功',
          position: 'center',
          icon: 'success'
        });
        // 延迟返回，让用户看到成功提示
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        throw new Error(response.data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新球员信息失败:', error);
      Toast.show({
        content: error.response?.data?.message || '更新失败，请重试',
        position: 'center',
        icon: 'fail'
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="mobile-edit-player">
        <NavBar
          onBack={handleBack}
          style={{ flexShrink: 0 }}
        >
          编辑球员信息
        </NavBar>
        <div className="loading-container">
          <div className="loading-text">加载中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-edit-player">
      <NavBar
        onBack={handleBack}
        style={{ flexShrink: 0 }}
      >
        编辑球员信息
      </NavBar>

      <div className="edit-player-content">
        <Card title="基本信息" className="info-card">
          <div className="form-group">
            <label className="form-label required">姓名</label>
            <Input
              placeholder="请输入姓名"
              value={formData.name}
              onChange={(val) => handleChange('name', val)}
              maxLength={20}
            />
          </div>

          <div className="form-group">
            <label className="form-label required">性别</label>
            <Radio.Group
              value={formData.gender}
              onChange={(val) => handleChange('gender', val)}
            >
              <Radio value="MALE">男</Radio>
              <Radio value="FEMALE">女</Radio>
            </Radio.Group>
          </div>

          <div className="form-group">
            <label className="form-label">年龄</label>
            <Input
              type="number"
              placeholder="请输入年龄（选填）"
              value={formData.age}
              onChange={(val) => handleChange('age', val)}
              min={0}
              max={120}
            />
          </div>

          <div className="form-group">
            <label className="form-label">水平</label>
            <Radio.Group
              value={formData.level || ''}
              onChange={(val) => handleChange('level', val || null)}
            >
              <Radio value="">不设置</Radio>
              <Radio value="初级">初级</Radio>
              <Radio value="中级">中级</Radio>
              <Radio value="高级">高级</Radio>
            </Radio.Group>
          </div>

          <div className="form-group">
            <label className="form-label">组织</label>
            <Input
              placeholder="请输入组织名称（选填）"
              value={formData.organization}
              onChange={(val) => handleChange('organization', val)}
              maxLength={50}
            />
          </div>

          <div className="form-group">
            <label className="form-label">手机号</label>
            <Input
              type="tel"
              placeholder="请输入手机号（选填）"
              value={formData.phone}
              onChange={(val) => handleChange('phone', val)}
              maxLength={11}
            />
          </div>

          <div className="form-group">
            <label className="form-label">邮箱</label>
            <Input
              type="email"
              placeholder="请输入邮箱（选填）"
              value={formData.email}
              onChange={(val) => handleChange('email', val)}
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label className="form-label">备注</label>
            <Input
              placeholder="请输入备注（选填）"
              value={formData.notes}
              onChange={(val) => handleChange('notes', val)}
              maxLength={200}
            />
          </div>
        </Card>
      </div>

      <div className="edit-player-actions">
        <Button
          color="primary"
          block
          size="large"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        >
          {loading ? '保存中...' : '保存修改'}
        </Button>
        <Button
          block
          size="large"
          onClick={handleBack}
          disabled={loading}
          style={{ marginTop: 12 }}
        >
          取消
        </Button>
      </div>
    </div>
  );
};

export default MobileEditPlayer;


